/**
 * Aba Benefícios do funcionário — HISTÓRICO TEMPORAL em cards (rodada 05/08/2026):
 * um card por benefício (tipo) com a LINHA DO TEMPO das vigências. Regra de ouro
 * (plano aprovado do dono): editar VALORES de vínculo vigente = NOVA VIGÊNCIA —
 * o atual encerra na competência anterior à mudança e o novo nasce com os valores
 * editados (meses fechados intactos); vínculo FUTURO corrige in place (nada
 * calculado); observação sem mudança de valores edita in place sempre; vínculo
 * ENCERRADO (23/09/2026) corrige in place via "corrigir este histórico" no
 * modal de histórico (alerta amarelo + confirmação; PATCH com corrigirHistorico). A DATA-BASE ("Ver o que vale em") escolhe o mês de referência
 * do status/valores; benefícios totalmente encerrados ficam na seção colapsada
 * "Encerrados". Ações legíveis: novo vínculo (tipo → campos do modelo → vigência
 * MM/AAAA), editar, encerrar (competência fim — nada é apagado).
 * Rodada 03/09/2026 (pedido do dono): DUAS VISUALIZAÇÕES — "Por mês" (os cards
 * com a data-base, como sempre) × "Visão geral" (lista única de TODOS os
 * benefícios que a pessoa já teve — vigentes, futuros e encerrados juntos, com
 * período total e status na data ATUAL). Cada benefício abre o
 * ModalHistoricoBeneficio: a timeline das vigências com "o que mudou aqui" e as
 * ações de QUEBRA ("Adicionar quebra" = o editor de vínculo existente — ele já
 * cria a vigência nova a partir do mês escolhido) e de encerramento. Os tipos e
 * helpers puros moram em web/src/beneficios-historico.ts.
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Checkbox, Group, Modal, NumberInput, SegmentedControl, Select, Text, TextInput, Timeline, Title, ThemeIcon } from "@mantine/core";
import { IconGift, IconHistory, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { api, ErroApi } from "../../api";
import { fmtCompetencia } from "../../format";
import { CompetenciaField, MoedaField } from "../../componentes/entradas-mantine";
import { notificarOk, notificarAviso, AvisosAlert, avisosDe } from "../../componentes/notificacoes";
import { Carregando } from "../../componentes/Estados";
import { ItemEntidade } from "../../componentes/listas";
import { ROTULO_MODELO } from "../BeneficiosPage";
import {
  agruparPorBeneficio,
  detalheDesconto,
  detalheVinculo,
  periodoTotalDaCadeia,
  statusVinculo,
  type GrupoBeneficio,
  type Vinculo,
} from "../../beneficios-historico";
import { TARIFA_LIVRE, rotuloTarifaCatalogo, tarifaLinhaDoVinculo, tarifaPayload, tarifaPreenchida, type TarifaCatalogo, type TarifaLinhaForm } from "../../beneficios-tarifas";
import { ModalHistoricoBeneficio } from "./ModalHistoricoBeneficio";

interface Tipo {
  id: string;
  nome: string;
  natureza: string;
  modeloCalculo: string;
  papel: string;
  ativo: boolean;
}

/** Tarefa A — papéis cujo vínculo pode ter desconto do funcionário (override de valor). */
const PAPEIS_COM_DESCONTO = ["vt", "vr", "auxilio_home_office", "seguro_vida"];

type BeneficiarioForm = { descricao: string; mensalidade: number | null; pf: number; pe: number };

function competenciaAtual(): number {
  const hoje = new Date();
  return hoje.getUTCFullYear() * 100 + (hoje.getUTCMonth() + 1);
}

/** Busca as tarifas VIGENTES do catálogo do tipo (papel vt) — vazio sem catálogo. */
function usarCatalogoTarifas(tipoId: string | null, papel: string | null): TarifaCatalogo[] {
  const [catalogo, setCatalogo] = useState<TarifaCatalogo[]>([]);
  useEffect(() => {
    if (!tipoId || papel !== "vt") {
      setCatalogo([]);
      return;
    }
    let vivo = true;
    api
      .get<{ vigentes?: TarifaCatalogo[] }>(`/beneficio-tipos/${tipoId}/tarifas`)
      .then((r) => vivo && setCatalogo(r.vigentes ?? []))
      .catch(() => vivo && setCatalogo([]));
    return () => {
      vivo = false;
    };
  }, [tipoId, papel]);
  return catalogo;
}

/** Editor de tarifas (VT — cap_salario_custo), usado no criar e no editar.
 *  Com CATÁLOGO do tipo (23/09/2026): cada linha é um Select das tarifas
 *  vigentes ("nome — R$ valor") com a opção "valor livre" — a linha legada
 *  (tarifaCentavos sem tarifaCatalogoId) abre em valor livre, com o input de
 *  valor ao lado, e troca para o catálogo pelo próprio Select. Sem catálogo,
 *  segue o valor livre de sempre. SEM limite de linhas ("+ adicionar tarifa"). */
function EditorTarifas({
  tarifas,
  setTarifas,
  catalogo,
}: {
  tarifas: TarifaLinhaForm[];
  setTarifas: (fn: (ls: TarifaLinhaForm[]) => TarifaLinhaForm[]) => void;
  catalogo: TarifaCatalogo[];
}) {
  const comCatalogo = catalogo.length > 0;
  const dadosSelect = [
    ...catalogo.map((c) => ({ value: c.id, label: rotuloTarifaCatalogo(c) })),
    { value: TARIFA_LIVRE, label: "valor livre (fora do catálogo)" },
  ];
  return (
    <Card withBorder mb="sm" padding="sm">
      <Text size="sm" fw={600} mb={4}>Tarifas por dia — o desconto é min(6% do salário, custo)</Text>
      {tarifas.map((t, i) => (
        <Group key={i} mb="xs" align="flex-end" wrap="nowrap">
          {comCatalogo ? (
            <>
              <Select
                label={i === 0 ? "Tarifa" : ""}
                data={dadosSelect}
                value={t.tarifaCatalogoId ?? TARIFA_LIVRE}
                onChange={(v) =>
                  setTarifas((ls) =>
                    ls.map((x, j) => (j === i ? (v && v !== TARIFA_LIVRE ? { ...x, tarifaCatalogoId: v, tarifa: null } : { ...x, tarifaCatalogoId: null }) : x)),
                  )
                }
                w={230}
              />
              {t.tarifaCatalogoId === null && (
                <MoedaField
                  label={i === 0 ? "Valor livre" : ""}
                  valor={t.tarifa}
                  onChange={(v) => setTarifas((ls) => ls.map((x, j) => (j === i ? { ...x, tarifa: v } : x)))}
                  w={110}
                />
              )}
            </>
          ) : (
            <MoedaField
              label={i === 0 ? "Tarifa" : ""}
              valor={t.tarifa}
              onChange={(v) => setTarifas((ls) => ls.map((x, j) => (j === i ? { ...x, tarifa: v } : x)))}
              w={120}
            />
          )}
          <NumberInput
            label={i === 0 ? "Qtd/dia" : ""}
            value={t.quantidade}
            onChange={(v) => setTarifas((ls) => ls.map((x, j) => (j === i ? { ...x, quantidade: Number(v) || 1 } : x)))}
            min={1}
            max={4}
            w={80}
          />
          {tarifas.length > 1 && (
            <Button variant="subtle" color="red" size="xs" onClick={() => setTarifas((ls) => ls.filter((_, j) => j !== i))}>
              <IconTrash size={14} />
            </Button>
          )}
        </Group>
      ))}
      <Button
        variant="subtle"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() => setTarifas((ls) => [...ls, { tarifaCatalogoId: comCatalogo ? catalogo[0]!.id : null, tarifa: null, quantidade: 2 }])}
      >
        + adicionar tarifa
      </Button>
      {tarifas.length > 1 && (
        <Button variant="subtle" color="red" size="xs" onClick={() => setTarifas(() => [{ tarifaCatalogoId: null, tarifa: null, quantidade: 2 }])}>
          zerar tarifas (sem desconto)
        </Button>
      )}
    </Card>
  );
}

/** Editor de beneficiários com rateio (plano_saude_rateio), criar e editar. */
function EditorBeneficiarios({
  beneficiarios,
  setBeneficiarios,
}: {
  beneficiarios: BeneficiarioForm[];
  setBeneficiarios: (fn: (ls: BeneficiarioForm[]) => BeneficiarioForm[]) => void;
}) {
  return (
    <Card withBorder mb="sm" padding="sm">
      <Text size="sm" fw={600} mb={4}>Beneficiários com rateio (% funcionário × % empresa)</Text>
      {beneficiarios.map((b, i) => (
        <Group key={i} mb="xs" align="flex-end" wrap="nowrap">
          <TextInput
            label={i === 0 ? "Beneficiário" : ""}
            value={b.descricao}
            onChange={(e) => {
              // o valor é lido ANTES do updater — dentro dele o currentTarget já é null
              const v = e.currentTarget.value;
              setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, descricao: v } : x)));
            }}
            w={160}
          />
          <MoedaField
            label={i === 0 ? "Mensalidade" : ""}
            valor={b.mensalidade}
            onChange={(v) => setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, mensalidade: v } : x)))}
            w={110}
          />
          <NumberInput label={i === 0 ? "% func." : ""} value={b.pf} onChange={(v) => setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, pf: Number(v) || 0 } : x)))} min={0} max={100} w={70} />
          <NumberInput label={i === 0 ? "% empresa" : ""} value={b.pe} onChange={(v) => setBeneficiarios((ls) => ls.map((x, j) => (j === i ? { ...x, pe: Number(v) || 0 } : x)))} min={0} max={100} w={80} />
          {beneficiarios.length > 1 && (
            <Button variant="subtle" color="red" size="xs" onClick={() => setBeneficiarios((ls) => ls.filter((_, j) => j !== i))}>
              <IconTrash size={14} />
            </Button>
          )}
        </Group>
      ))}
      <Button
        variant="subtle"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() => setBeneficiarios((ls) => [...ls, { descricao: "dependente: ", mensalidade: null, pf: 50, pe: 50 }])}
      >
        beneficiário
      </Button>
      <Text size="xs" c="dimmed" mt={4}>
        O desconto é a soma de mensalidade × % funcionário. Casos sem desconto em folha usam % funcionário 0.
      </Text>
    </Card>
  );
}

/** Form de vínculo: tipo escolhido → campos do modelo de cálculo. */
function ModalVinculo({
  funcionarioId,
  tipos,
  onFechar,
  onSalvo,
}: {
  funcionarioId: string;
  tipos: Tipo[];
  onFechar: () => void;
  onSalvo: (avisos: string[]) => void;
}) {
  const [tipoId, setTipoId] = useState<string | null>(null);
  const tipo = tipos.find((t) => t.id === tipoId);
  const [inicio, setInicio] = useState<number | null>(competenciaAtual());
  const [fim, setFim] = useState<number | null>(null);
  const [valor, setValor] = useState<number | null>(null);
  const [descontoValor, setDescontoValor] = useState<number | null>(null);
  const [observacao, setObservacao] = useState("");
  const [tarifas, setTarifas] = useState<TarifaLinhaForm[]>([{ tarifaCatalogoId: null, tarifa: null, quantidade: 2 }]);
  const catalogoTarifas = usarCatalogoTarifas(tipo?.id ?? null, tipo?.papel ?? null);
  const [beneficiarios, setBeneficiarios] = useState<BeneficiarioForm[]>([
    { descricao: "titular", mensalidade: null, pf: 10, pe: 90 },
  ]);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setErro(null);
    try {
      const resposta = await api.post(`/funcionarios/${funcionarioId}/beneficios`, {
        beneficioTipoId: tipoId,
        vigenciaInicio: inicio,
        vigenciaFim: fim,
        valorCentavos: valor,
        descontoValorCentavos: tipo && PAPEIS_COM_DESCONTO.includes(tipo.papel) ? descontoValor : undefined,
        observacao: observacao || null,
        tarifas: tipo?.modeloCalculo === "cap_salario_custo" ? tarifas.filter(tarifaPreenchida).map(tarifaPayload) : undefined,
        beneficiarios:
          tipo?.modeloCalculo === "plano_saude_rateio"
            ? beneficiarios
                .filter((b) => b.mensalidade !== null)
                .map((b) => ({ descricao: b.descricao || "beneficiário", mensalidadeCentavos: b.mensalidade!, percentualFuncionario: b.pf, percentualEmpresa: b.pe }))
            : undefined,
      });
      notificarOk("Vínculo criado — já vale a partir da competência escolhida.");
      onSalvo(avisosDe(resposta));
      onFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  return (
    <Modal opened onClose={onFechar} title="Novo vínculo de benefício" size="lg" centered>
      <Select
        label="Tipo de benefício"
        placeholder="escolha…"
        data={tipos.map((t) => ({ value: t.id, label: `${t.nome} (${ROTULO_MODELO[t.modeloCalculo] ?? t.modeloCalculo})` }))}
        value={tipoId}
        onChange={setTipoId}
        searchable
        mb="sm"
      />
      <Group grow mb="sm">
        <CompetenciaField label="Vale a partir de" valor={inicio} onChange={setInicio} />
        <div>
          <CompetenciaField label="Até (vazio = em vigor)" valor={fim} onChange={setFim} anoMax={2030} />
          {fim !== null && (
            <Button variant="subtle" size="xs" onClick={() => setFim(null)}>
              limpar fim
            </Button>
          )}
        </div>
      </Group>
      {(tipo?.modeloCalculo === "valor_fixo_mensal" || tipo?.modeloCalculo === "valor_diario") && (
        <MoedaField
          label={tipo.modeloCalculo === "valor_diario" ? "Valor por dia (override do vínculo)" : "Valor mensal (override do vínculo)"}
          description="Vazio = usa o valor do parâmetro global vigente no mês."
          valor={valor}
          onChange={setValor}
          mb="sm"
        />
      )}
      {tipo?.modeloCalculo === "cap_salario_custo" && <EditorTarifas tarifas={tarifas} setTarifas={setTarifas} catalogo={catalogoTarifas} />}
      {tipo?.modeloCalculo === "plano_saude_rateio" && <EditorBeneficiarios beneficiarios={beneficiarios} setBeneficiarios={setBeneficiarios} />}
      {tipo && PAPEIS_COM_DESCONTO.includes(tipo.papel) && (
        <MoedaField
          label="Desconto do funcionário — valor override (opcional)"
          description="Vazio = o valor do tipo/parâmetro global. Só vale nos modelos de desconto em R$ (valor fixo / limite de valor)."
          valor={descontoValor}
          onChange={setDescontoValor}
          mb="sm"
        />
      )}
      <TextInput label="Observação (opcional)" value={observacao} onChange={(e) => setObservacao(e.currentTarget.value)} mb="sm" />
      {erro && (
        <Text size="sm" c="red.7" mb="sm" role="alert">
          {erro}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>
          Voltar
        </Button>
        <Button onClick={() => void salvar()} disabled={!tipoId || inicio === null}>
          Criar vínculo
        </Button>
      </Group>
    </Modal>
  );
}

/** Detalhe/edição do vínculo: campos do modelo já preenchidos + observação.
 *  REGRA DE OURO (05/08/2026): mudar VALORES de vínculo vigente = NOVA VIGÊNCIA
 *  (o usuário escolhe "vale a partir de"; o atual encerra no mês anterior);
 *  vínculo FUTURO corrige in place; só observação = in place (não muda cálculo).
 *  Vínculo ENCERRADO (23/09/2026 — correção de histórico): alerta amarelo +
 *  confirmação explícita; o PATCH vai com `corrigirHistorico: true` e aplica in
 *  place (as reproduções dos meses cobertos passam a usar os novos valores; os
 *  meses fechados têm o snapshot das runs). Os avisos da resposta viram toasts.
 *  `comoQuebra` (03/09/2026): aberto pelo botão "Adicionar quebra" do modal de
 *  histórico — a mecânica é EXATAMENTE a mesma, só o título muda. */
function ModalEditarVinculo({
  vinculo,
  comoQuebra,
  onFechar,
  onSalvo,
}: {
  vinculo: Vinculo;
  comoQuebra?: boolean;
  onFechar: () => void;
  onSalvo: (avisos: string[]) => void;
}) {
  const modelo = vinculo.tipoModeloCalculo;
  const compAtual = competenciaAtual();
  const ehVigente = vinculo.vigenciaInicio <= compAtual && (vinculo.vigenciaFim === null || vinculo.vigenciaFim >= compAtual);
  const ehEncerrado = vinculo.vigenciaFim !== null && vinculo.vigenciaFim < compAtual;
  const [valor, setValor] = useState<number | null>(vinculo.valorCentavos);
  const [descontoValor, setDescontoValor] = useState<number | null>(vinculo.descontoValorCentavos ?? null);
  const [observacao, setObservacao] = useState(vinculo.observacao ?? "");
  const [mudanca, setMudanca] = useState<number | null>(compAtual);
  const [confirmouCorrecao, setConfirmouCorrecao] = useState(false);
  const [tarifas, setTarifas] = useState<TarifaLinhaForm[]>(
    vinculo.tarifas.length > 0 ? vinculo.tarifas.map(tarifaLinhaDoVinculo) : [{ tarifaCatalogoId: null, tarifa: null, quantidade: 2 }],
  );
  const catalogoTarifas = usarCatalogoTarifas(modelo === "cap_salario_custo" ? vinculo.beneficioTipoId : null, vinculo.tipoPapel);
  const [beneficiarios, setBeneficiarios] = useState<BeneficiarioForm[]>(
    vinculo.beneficiarios.length > 0
      ? vinculo.beneficiarios.map((b) => ({
          descricao: b.descricao,
          mensalidade: b.mensalidadeCentavos,
          pf: b.percentualFuncionario,
          pe: b.percentualEmpresa,
        }))
      : [{ descricao: "titular", mensalidade: null, pf: 10, pe: 90 }],
  );
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setErro(null);
    // payload só com o que MUDOU: observação sozinha = edição in place (não muda
    // cálculo, não cria vigência); valores em vínculo vigente = nova vigência
    const tarifasNovas = modelo === "cap_salario_custo" ? tarifas.filter(tarifaPreenchida).map(tarifaPayload) : undefined;
    const tarifasAtuais = modelo === "cap_salario_custo" ? vinculo.tarifas.map((t) => tarifaPayload(tarifaLinhaDoVinculo(t))) : undefined;
    const beneficiariosNovos =
      modelo === "plano_saude_rateio"
        ? beneficiarios
            .filter((b) => b.mensalidade !== null)
            .map((b) => ({ descricao: b.descricao || "beneficiário", mensalidadeCentavos: b.mensalidade!, percentualFuncionario: b.pf, percentualEmpresa: b.pe }))
        : undefined;
    const valorMudou = (modelo === "valor_fixo_mensal" || modelo === "valor_diario") && valor !== vinculo.valorCentavos;
    const descontoMudou = PAPEIS_COM_DESCONTO.includes(vinculo.tipoPapel) && descontoValor !== (vinculo.descontoValorCentavos ?? null);
    const tarifasMudaram = JSON.stringify(tarifasNovas) !== JSON.stringify(tarifasAtuais);
    const beneficiariosMudaram =
      modelo === "plano_saude_rateio" && JSON.stringify(beneficiariosNovos) !== JSON.stringify(vinculo.beneficiarios);
    const obsMudou = (observacao || null) !== vinculo.observacao;
    const mudouValores = valorMudou || descontoMudou || tarifasMudaram || beneficiariosMudaram;
    if (!mudouValores && !obsMudou) {
      onFechar();
      return;
    }
    try {
      const payload: Record<string, unknown> = {};
      if (valorMudou) payload.valorCentavos = valor;
      if (descontoMudou) payload.descontoValorCentavos = descontoValor;
      if (tarifasMudaram) payload.tarifas = tarifasNovas;
      if (beneficiariosMudaram) payload.beneficiarios = beneficiariosNovos;
      if (obsMudou) payload.observacao = observacao || null;
      if (mudouValores && ehVigente) payload.vigenciaMudanca = mudanca;
      // correção de histórico (23/09/2026): vínculo encerrado edita IN PLACE
      if (ehEncerrado) payload.corrigirHistorico = true;
      const resposta = await api.patch(`/beneficio-vinculos/${vinculo.id}`, payload);
      const r = resposta as { substituiu?: string; vigenciaInicio?: number };
      const avisos = avisosDe(resposta);
      if (ehEncerrado) {
        notificarOk(`"${vinculo.tipoNome}" corrigido no histórico.`);
        avisos.forEach((a) => notificarAviso(a));
      } else {
        notificarOk(
          r.substituiu
            ? `Nova vigência de "${vinculo.tipoNome}" a partir de ${fmtCompetencia(r.vigenciaInicio!)} — o vínculo anterior e os meses fechados ficam preservados.`
            : `"${vinculo.tipoNome}" atualizado.`,
        );
      }
      onSalvo(ehEncerrado ? [] : avisos);
      onFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  return (
    <Modal opened onClose={onFechar} title={comoQuebra ? `Nova quebra de "${vinculo.tipoNome}"` : `Editar — ${vinculo.tipoNome}`} size="lg" centered>
      <Text size="xs" c="dimmed" mb="sm">
        Vigência {fmtCompetencia(vinculo.vigenciaInicio)} → {vinculo.vigenciaFim ? fmtCompetencia(vinculo.vigenciaFim) : "em vigor"} ·{" "}
        {ROTULO_MODELO[modelo] ?? modelo}.
      </Text>
      {ehVigente && (
        <Card withBorder mb="sm" padding="sm">
          <Text size="xs" mb="xs">
            <strong>Mudança com histórico preservado:</strong> ao salvar valores novos, este vínculo é encerrado no mês
            anterior e um <strong>novo vínculo</strong> nasce com eles — os meses já fechados não mudam. (Só a observação?
            Edita direto, sem nova vigência.)
          </Text>
          <CompetenciaField label="Os novos valores valem a partir de" valor={mudanca} onChange={setMudanca} />
        </Card>
      )}
      {!ehVigente && vinculo.vigenciaInicio > compAtual && (
        <Text size="xs" c="dimmed" mb="sm">
          Este vínculo ainda não vigorou — a correção é direta, sem criar nova vigência.
        </Text>
      )}
      {ehEncerrado && (
        <Alert color="yellow" title="Correção de histórico" mb="sm">
          <Text size="sm" mb="xs">
            Este vínculo está encerrado desde {fmtCompetencia(vinculo.vigenciaFim!)}. A correção é feita no histórico: as
            reproduções dos meses cobertos passam a usar os novos valores (meses fechados têm o snapshot das runs).
          </Text>
          <Checkbox
            label="Confirmo que é uma correção do histórico"
            checked={confirmouCorrecao}
            onChange={(e) => setConfirmouCorrecao(e.currentTarget.checked)}
          />
        </Alert>
      )}
      {(modelo === "valor_fixo_mensal" || modelo === "valor_diario") && (
        <MoedaField
          label={modelo === "valor_diario" ? "Valor por dia (override do vínculo)" : "Valor mensal (override do vínculo)"}
          description="Vazio = usa o valor do parâmetro global vigente no mês."
          valor={valor}
          onChange={setValor}
          mb="sm"
        />
      )}
      {modelo === "cap_salario_custo" && <EditorTarifas tarifas={tarifas} setTarifas={setTarifas} catalogo={catalogoTarifas} />}
      {modelo === "plano_saude_rateio" && <EditorBeneficiarios beneficiarios={beneficiarios} setBeneficiarios={setBeneficiarios} />}
      {modelo === "custom_informativo" && (
        <Text size="xs" c="dimmed" mb="sm">Benefício informativo: não entra no cálculo da folha — só registro/histórico.</Text>
      )}
      {PAPEIS_COM_DESCONTO.includes(vinculo.tipoPapel) && (
        <MoedaField
          label="Desconto do funcionário — valor override (opcional)"
          description="Vazio = o valor do tipo/parâmetro global. Só vale nos modelos de desconto em R$ (valor fixo / limite de valor)."
          valor={descontoValor}
          onChange={setDescontoValor}
          mb="sm"
        />
      )}
      <TextInput label="Observação" value={observacao} onChange={(e) => setObservacao(e.currentTarget.value)} mb="sm" />
      {erro && (
        <Text size="sm" c="red.7" mb="sm" role="alert">
          {erro}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>
          Voltar
        </Button>
        <Button onClick={() => void salvar()} disabled={(ehVigente && mudanca === null) || (ehEncerrado && !confirmouCorrecao)}>
          Salvar alterações
        </Button>
      </Group>
    </Modal>
  );
}

/** Card de um benefício: vigência em destaque na data-base + linha do tempo.
 *  03/09/2026: ação "histórico" abre o modal com a timeline de alterações. */
function CardBeneficio({
  grupo,
  compRef,
  onEditar,
  onEncerrar,
  onHistorico,
}: {
  grupo: GrupoBeneficio;
  compRef: number;
  onEditar: (v: Vinculo) => void;
  onEncerrar: (v: Vinculo) => void;
  onHistorico: () => void;
}) {
  const { principal, cadeia } = grupo;
  const st = statusVinculo(principal, compRef);
  // linha do tempo: mais recente primeiro (padrão do histórico de contrato)
  const linhaDoTempo = [...cadeia].reverse();
  return (
    <Card withBorder padding="sm" radius="md" mb={6} style={grupo.encerrado ? { opacity: 0.75 } : undefined}>
      <Group justify="space-between" wrap="nowrap" mb={4}>
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon variant="light" size="lg">
            <IconGift size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm" truncate>
            {grupo.tipoNome}
          </Text>
          <Badge color={st.cor}>{st.rotulo}</Badge>
        </Group>
        <Group gap={6} wrap="nowrap">
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconHistory size={14} />}
            onClick={onHistorico}
            title="Histórico de alterações deste benefício — cada quebra é uma vigência"
          >
            histórico
          </Button>
          {!grupo.encerrado && (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconPencil size={14} />}
              onClick={() => onEditar(principal)}
              title="Editar valores/observação — em vínculo vigente, valores novos viram uma NOVA vigência (o histórico é preservado)"
            >
              editar
            </Button>
          )}
          {principal.vigenciaFim === null && (
            <Button size="xs" variant="subtle" color="yellow.8" onClick={() => onEncerrar(principal)} title="Encerrar a vigência — nada é apagado">
              encerrar
            </Button>
          )}
        </Group>
      </Group>
      <Text size="sm">
        {fmtCompetencia(principal.vigenciaInicio)} → {principal.vigenciaFim ? fmtCompetencia(principal.vigenciaFim) : "em vigor"} ·{" "}
        {detalheVinculo(principal)}
        {detalheDesconto(principal)}
      </Text>
      {principal.observacao && (
        <Text size="xs" c="dimmed" mt={2}>
          {principal.observacao}
        </Text>
      )}
      {cadeia.length > 1 && (
        <>
          <Text size="xs" c="dimmed" mt="sm" mb={2}>
            Linha do tempo ({cadeia.length} vigências — cada mudança de valores é uma vigência):
          </Text>
          <Timeline active={linhaDoTempo.findIndex((v) => v.id === principal.id)} bulletSize={14} lineWidth={1}>
            {linhaDoTempo.map((v) => {
              const sti = statusVinculo(v, compRef);
              return (
                <Timeline.Item
                  key={v.id}
                  title={
                    <Text size="xs">
                      {fmtCompetencia(v.vigenciaInicio)} → {v.vigenciaFim ? fmtCompetencia(v.vigenciaFim) : "em vigor"}
                      <Badge ml={6} size="xs" color={sti.cor}>
                        {sti.rotulo}
                      </Badge>
                      {v.id === principal.id && (
                        <Badge ml={4} size="xs" variant="outline" color="dark">
                          na data-base
                        </Badge>
                      )}
                    </Text>
                  }
                >
                  <Text size="xs" c="dimmed">
                    {detalheVinculo(v)}
                    {detalheDesconto(v)}
                    {v.observacao ? ` · ${v.observacao}` : ""}
                  </Text>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </>
      )}
    </Card>
  );
}

export function BeneficiosTab({ funcionarioId }: { funcionarioId: string }) {
  const [vinculos, setVinculos] = useState<Vinculo[] | null>(null);
  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [criando, setCriando] = useState(false);
  // P2 — avisos de carga histórica do último vínculo criado/editado (meses fechados cobertos)
  const [avisosVinculo, setAvisosVinculo] = useState<string[]>([]);
  const [editando, setEditando] = useState<Vinculo | null>(null);
  // 03/09/2026 — o editor aberto pelo "Adicionar quebra" do modal de histórico
  // (mesma mecânica; só o título muda para "Nova quebra de …")
  const [editandoQuebra, setEditandoQuebra] = useState(false);
  const [encerrando, setEncerrando] = useState<Vinculo | null>(null);
  const [fimEncerramento, setFimEncerramento] = useState<number | null>(competenciaAtual());
  const [erroEncerramento, setErroEncerramento] = useState<string | null>(null);
  // DATA-BASE: o mês de referência do status/valores exibidos (default: o atual)
  const [compRef, setCompRef] = useState<number>(competenciaAtual());
  const [mostrarEncerrados, setMostrarEncerrados] = useState(false);
  // 03/09/2026 — as duas visualizações pedidas pelo dono: "Por mês" (cards com a
  // data-base, como sempre) × "Visão geral" (lista única de todos os benefícios)
  const [visao, setVisao] = useState<"mes" | "geral">("mes");
  // o benefício selecionado com o histórico aberto (grupo + a data-base do status)
  const [historico, setHistorico] = useState<{ grupo: GrupoBeneficio; compRef: number } | null>(null);

  async function carregar() {
    try {
      const [v, t] = await Promise.all([
        api.get<{ vinculos: Vinculo[] }>(`/funcionarios/${funcionarioId}/beneficios`),
        api.get<{ tipos: Tipo[] }>("/beneficio-tipos"),
      ]);
      setVinculos(v.vinculos);
      setTipos(t.tipos);
    } catch (e) {
      setErro(e);
    }
  }
  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funcionarioId]);

  async function encerrar() {
    if (!encerrando) return;
    setErroEncerramento(null);
    try {
      await api.post(`/beneficio-vinculos/${encerrando.id}/encerrar`, { vigenciaFim: fimEncerramento });
      notificarOk(`"${encerrando.tipoNome}" encerrado — o histórico fica preservado.`);
      setEncerrando(null);
      setHistorico(null); // a cadeia mudou — o modal de histórico não fica com dados velhos
      await carregar();
    } catch (e) {
      setErroEncerramento(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!vinculos) return <Carregando linhas={3} />;

  const tiposSemVinculoAtivo = tipos.filter((t) => t.ativo && !vinculos.some((v) => v.beneficioTipoId === t.id && v.vigenciaFim === null));
  const compHoje = competenciaAtual();
  // "Por mês": agrupa na DATA-BASE; "Visão geral": agrupa na data ATUAL (o status
  // da lista é o de hoje — pedido do dono 03/09/2026) e mostra TODOS os grupos
  const grupos = agruparPorBeneficio(vinculos, visao === "mes" ? compRef : compHoje);
  const emVigor = grupos.filter((g) => !g.encerrado);
  const encerrados = grupos.filter((g) => g.encerrado);

  return (
    <>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Title order={5}>Benefícios desta pessoa (com vigência)</Title>
        <Group gap="md" wrap="nowrap">
          <SegmentedControl
            size="xs"
            value={visao}
            onChange={(v) => setVisao(v as "mes" | "geral")}
            data={[
              { value: "mes", label: "Por mês" },
              { value: "geral", label: "Visão geral" },
            ]}
          />
          {visao === "mes" && <CompetenciaField label="Ver o que vale em" valor={compRef} onChange={setCompRef} />}
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            variant="light"
            onClick={() => setCriando(true)}
            disabled={tiposSemVinculoAtivo.length === 0}
            title={tiposSemVinculoAtivo.length === 0 ? "Todos os tipos já têm vínculo em vigor" : undefined}
          >
            Novo vínculo
          </Button>
        </Group>
      </Group>
      <Text size="xs" c="dimmed" mb="md">
        {visao === "mes" ? (
          <>
            Um card por benefício, com a <strong>linha do tempo das vigências</strong>: cada mudança de valores encerra a
            vigência atual e cria a nova — os meses já fechados nunca mudam. A data-base escolhe o mês que os status e
            valores refletem; encerrar preserva o histórico (nada é apagado). Ação "histórico" no card abre o histórico de
            alterações completo, com as quebras.
          </>
        ) : (
          <>
            Todos os benefícios que a pessoa já teve — vigentes, futuros e encerrados juntos, com o período total e o
            status <strong>na data atual</strong>. Clique em um benefício para ver o histórico de modificações e cadastrar
            quebras ("a partir de março, 200; abril cancela").
          </>
        )}
      </Text>
      <AvisosAlert avisos={avisosVinculo} />

      {vinculos.length === 0 && <Text c="dimmed" size="sm">Nenhum benefício vinculado — use "Novo vínculo".</Text>}
      {visao === "mes" && (
        <>
          {emVigor.map((g) => (
            <CardBeneficio
              key={g.tipoId}
              grupo={g}
              compRef={compRef}
              onEditar={(v) => {
                setEditando(v);
                setEditandoQuebra(false);
              }}
              onEncerrar={setEncerrando}
              onHistorico={() => setHistorico({ grupo: g, compRef })}
            />
          ))}
          {encerrados.length > 0 && (
            <>
              <Button variant="subtle" size="xs" mt="sm" onClick={() => setMostrarEncerrados((m) => !m)}>
                {mostrarEncerrados ? "Ocultar" : "Mostrar"} encerrados ({encerrados.length})
              </Button>
              {mostrarEncerrados &&
                encerrados.map((g) => (
                  <CardBeneficio
                    key={g.tipoId}
                    grupo={g}
                    compRef={compRef}
                    onEditar={(v) => {
                      setEditando(v);
                      setEditandoQuebra(false);
                    }}
                    onEncerrar={setEncerrando}
                    onHistorico={() => setHistorico({ grupo: g, compRef })}
                  />
                ))}
            </>
          )}
        </>
      )}
      {visao === "geral" &&
        grupos.map((g) => {
          const periodo = periodoTotalDaCadeia(g.cadeia);
          const stg = statusVinculo(g.principal, compHoje);
          return (
            <ItemEntidade
              key={g.tipoId}
              icone={
                <ThemeIcon variant="light" size="lg">
                  <IconGift size={18} />
                </ThemeIcon>
              }
              titulo={g.tipoNome}
              linhaSecundaria={`${fmtCompetencia(periodo.inicio)} → ${periodo.fim === null ? "em vigor" : fmtCompetencia(periodo.fim)} · ${g.cadeia.length} ${g.cadeia.length === 1 ? "vigência" : "vigências"}`}
              badges={<Badge color={stg.cor}>{stg.rotulo}</Badge>}
              opaco={stg.rotulo === "encerrado"}
              onClick={() => setHistorico({ grupo: g, compRef: compHoje })}
              ariaLabel={`Histórico de ${g.tipoNome}`}
            />
          );
        })}

      {criando && (
        <ModalVinculo
          funcionarioId={funcionarioId}
          tipos={tiposSemVinculoAtivo}
          onFechar={() => setCriando(false)}
          onSalvo={async (avisos) => {
            setAvisosVinculo(avisos);
            await carregar();
          }}
        />
      )}

      {historico && (
        <ModalHistoricoBeneficio
          grupo={historico.grupo}
          compRef={historico.compRef}
          onFechar={() => setHistorico(null)}
          onAdicionarQuebra={(v) => {
            setEditando(v);
            setEditandoQuebra(true);
          }}
          onEncerrar={setEncerrando}
          onCorrigir={(v) => {
            setEditando(v);
            setEditandoQuebra(false);
          }}
        />
      )}

      {editando && (
        <ModalEditarVinculo
          vinculo={editando}
          comoQuebra={editandoQuebra}
          onFechar={() => {
            setEditando(null);
            setEditandoQuebra(false);
          }}
          onSalvo={async (avisos) => {
            setAvisosVinculo(avisos);
            setHistorico(null); // salvou a quebra: fecha o histórico (a cadeia mudou) e recarrega
            await carregar();
          }}
        />
      )}

      <Modal opened={encerrando !== null} onClose={() => setEncerrando(null)} title={`Encerrar "${encerrando?.tipoNome}"?`} centered>
        <Text size="sm" mb="sm">
          O vínculo deixa de valer a partir do mês seguinte ao fim escolhido — mas <strong>nada é apagado</strong>: o
          histórico de quando ele valeu (e com que valores) fica preservado aqui e na folha já fechada.
        </Text>
        <CompetenciaField label="Última competência vigente" valor={fimEncerramento} onChange={setFimEncerramento} anoMax={2030} />
        {erroEncerramento && (
          <Text size="sm" c="red.7" mt="sm" role="alert">
            {erroEncerramento}
          </Text>
        )}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setEncerrando(null)}>
            Voltar
          </Button>
          <Button color="yellow.8" onClick={() => void encerrar()} disabled={fimEncerramento === null}>
            Encerrar vínculo
          </Button>
        </Group>
      </Modal>
    </>
  );
}
